import { createContext, useContext, useEffect, useState, useRef } from 'react';
import { io } from 'socket.io-client';
import { SOCKET_EVENTS, API_CONFIG } from 'api/constants';
import apiConfig from 'api/config';

const SocketContext = createContext({
  socket: null,
  isConnected: false,
  connect: () => {},
  disconnect: () => {},
  emit: () => {},
  on: () => {},
  off: () => {},
});

export const useSocket = () => {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error('useSocket must be used within a SocketProvider');
  }
  return context;
};

// Get WebSocket URL from API base URL
const getSocketUrl = () => {
  const apiUrl = API_CONFIG.BASE_URL;
  // Remove /api from the end if exists
  const baseUrl = apiUrl.replace(/\/api\/?$/, '');
  console.log('🔌 Socket URL:', baseUrl);
  return baseUrl;
};

export const SocketProvider = ({ children }) => {
  const [socket, setSocket] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const socketRef = useRef(null);
  const reconnectAttempts = useRef(0);
  const maxReconnectAttempts = 5;

  // Server URL - API config'den al
  const serverUrl = getSocketUrl();

  const connect = () => {
    // Token'i localStorage'dan al
    const token = apiConfig.getAuthToken();
    
    if (socketRef.current?.connected) {
      console.log('🔌 Socket already connected');
      return;
    }

    if (!token) {
      console.log('⚠️ No auth token found, skipping socket connection');
      return;
    }

    console.log('🔌 Connecting to WebSocket server:', serverUrl);
    console.log('🔌 Admin token:', token ? 'Present ✅' : 'Missing ❌');

    try {
      const newSocket = io(serverUrl, {
        transports: ['websocket', 'polling'], // Add polling as fallback
        autoConnect: true,
        auth: {
          token: token
        },
        reconnection: true,
        reconnectionAttempts: maxReconnectAttempts,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000,
        timeout: 20000,
      });

      // Connection events
      newSocket.on('connect', () => {
        console.log('🔌 ========== WEBSOCKET CONNECTED ==========');
        console.log('🔌 Socket ID:', newSocket.id);
        console.log('🔌 Server:', serverUrl);
        console.log('🔌 Transport:', newSocket.io.engine.transport.name);
        
        setIsConnected(true);
        reconnectAttempts.current = 0;
        
        // Test connection
        newSocket.emit(SOCKET_EVENTS.TEST_CONNECTION, { 
          message: 'Admin connected',
          timestamp: new Date().toISOString()
        });
      });

      newSocket.on('disconnect', (reason) => {
        console.log('❌ WebSocket disconnected:', reason);
        setIsConnected(false);
      });

      newSocket.on('connect_error', (error) => {
        console.error('❌ Connection error:', error.message || error);
        reconnectAttempts.current += 1;
        
        if (reconnectAttempts.current >= maxReconnectAttempts) {
          console.error('❌ Max reconnection attempts reached');
          newSocket.disconnect();
        }
      });

      newSocket.on('error', (error) => {
        console.error('❌ Socket error:', error);
      });

      // Debug: Listen to all events
      newSocket.onAny((event, data) => {
        console.log('🔔 Event received:', event);
        console.log('📦 Data:', data);
      });

      socketRef.current = newSocket;
      setSocket(newSocket);
    } catch (error) {
      console.error('❌ Socket connection error:', error);
    }
  };

  const disconnect = () => {
    if (socketRef.current) {
      console.log('🔌 Disconnecting WebSocket...');
      socketRef.current.disconnect();
      socketRef.current = null;
      setSocket(null);
      setIsConnected(false);
    }
  };

  const emit = (event, data) => {
    if (socketRef.current && isConnected) {
      console.log('📤 Emitting event:', event, data);
      socketRef.current.emit(event, data);
    } else {
      console.warn('⚠️ Cannot emit - socket not connected');
    }
  };

  const on = (event, callback) => {
    if (socketRef.current) {
      socketRef.current.on(event, callback);
    }
  };

  const off = (event, callback) => {
    if (socketRef.current) {
      if (callback) {
        socketRef.current.off(event, callback);
      } else {
        socketRef.current.off(event);
      }
    }
  };

  // Auto connect when component mounts and token is available
  useEffect(() => {
    const token = apiConfig.getAuthToken();
    
    if (token && !socketRef.current) {
      console.log('🔌 Token found, connecting socket...');
      connect();
    }

    // Cleanup on unmount
    return () => {
      if (socketRef.current) {
        console.log('🧹 Cleaning up socket on unmount');
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    };
  }, []);

  // Listen to token changes
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'accessToken') {
        const token = e.newValue;
        
        if (token && !socketRef.current) {
          console.log('🔌 Token added, connecting socket...');
          connect();
        } else if (!token && socketRef.current) {
          console.log('🔌 Token removed, disconnecting socket...');
          disconnect();
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  const value = {
    socket,
    isConnected,
    connect,
    disconnect,
    emit,
    on,
    off,
  };

  return (
    <SocketContext.Provider value={value}>
      {children}
    </SocketContext.Provider>
  );
};

export default SocketContext;
